/**
 * Per-project, per-file memo of duplication warnings `register_edit` has
 * already told this agent about.
 *
 * The warning is worth its tokens the first time and worth nothing the
 * fortieth. `register_edit` recomputes `checkFileForDuplicates` over the whole
 * file on every edit, so editing one file forty times in a session reported the
 * same pre-existing similarities forty times — measured at 84-87% of the
 * tool's whole response, on the fourth busiest tool in the product (TRA-1098).
 *
 * The obvious fix — snapshot the file's symbols before reindexing and report
 * only what is new — does not work here, and the reason is worth recording.
 * A single edit fires three independent reindex paths (parcel-watcher inside
 * the daemon, the PostToolUse HTTP hook, and the agent's own `register_edit`
 * call); `recent-reindex-cache.ts` documents skew between them "regularly
 * exceeding 500ms", which is why its dedup TTL is 2000ms. If the watcher or
 * the hook reindexes first and the agent's call arrives later than that, the
 * "pre-edit" snapshot is read from a store that already reflects the edit, and
 * the genuinely new symbol is filtered out as pre-existing. That failure is
 * silent and it is worse than the noise it replaces.
 *
 * This memo does not read the store at all, so it cannot lose that race. A
 * warning the agent has not been shown is new *to the agent*, whoever indexed
 * the file, and that is the only sense in which "new" was ever actionable.
 *
 * Bounded on both axes, same shape as `recent-reindex-cache.ts`:
 *   - inner set: <= 32 warning pairs per file
 *   - outer map: <= 256 files, drops the least-recently-used on overflow
 *
 * ponytail: process-local and never invalidated. An agent that introduces a
 * duplicate, removes it, and reintroduces it inside one process is told once,
 * not twice. Persist per-file content hashes alongside the pairs if that ever
 * turns out to matter.
 */
const MAX_FILES = 256;
const MAX_PAIRS_PER_FILE = 32;

const seen = new Map<string, Set<string>>();

const keyFor = (project: string, filePath: string): string => `${project} ${filePath}`;
const pairFor = (sourceSymbolId: string, duplicateSymbolId: string): string =>
  `${sourceSymbolId} ${duplicateSymbolId}`;

/**
 * Split `warnings` into the ones this agent has not been shown for this file
 * and the rest, recording the returned ones as shown. Unseen warnings keep
 * their relative order and come first, so a caller that truncates the list
 * cannot drop a new finding in favour of one it already reported.
 */
export function takeUnreportedDuplicates<
  T extends { source_symbol_id: string; duplicate_symbol_id: string },
>(project: string, filePath: string, warnings: T[]): T[] {
  const key = keyFor(project, filePath);
  let bucket = seen.get(key);
  if (!bucket) {
    bucket = new Set();
    if (seen.size >= MAX_FILES) {
      // Map iteration is insertion order, so the first key is the LRU.
      const lru = seen.keys().next().value;
      if (lru !== undefined && lru !== key) seen.delete(lru);
    }
  } else {
    // delete + set bumps this file to most-recently-used.
    seen.delete(key);
  }
  seen.set(key, bucket);

  const fresh = warnings.filter(
    (w) => !bucket.has(pairFor(w.source_symbol_id, w.duplicate_symbol_id)),
  );
  for (const w of fresh) {
    if (bucket.size >= MAX_PAIRS_PER_FILE) break;
    bucket.add(pairFor(w.source_symbol_id, w.duplicate_symbol_id));
  }
  return fresh;
}

/** Test hook — drops every memo. Not called in production. */
export function resetDuplicationMemo(): void {
  seen.clear();
}
